import { format, startOfWeek, endOfWeek, addDays, isSameDay, addHours, startOfDay } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"
import { useState } from "react"

interface WeekViewProps {
  currentDate: Date
  events: CalendarEvent[]
  dateRange: { start: Date; end: Date }
  onViewEvent?: (event: CalendarEvent) => void
  onEditEvent?: (event: CalendarEvent) => void
  onDeleteEvent?: (event: CalendarEvent) => void
  refetch?: () => void
  access?: string
  hideEventActions?: boolean
  onDateClick?: (date: Date) => void
}

// Map color to static Tailwind classes
const getColorClasses = (color?: string) => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-200 border-purple-400 text-purple-900 hover:bg-purple-250",
    blue: "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250",
    green: "bg-green-200 border-green-400 text-green-900 hover:bg-green-250",
    red: "bg-red-200 border-red-400 text-red-900 hover:bg-red-250",
    yellow: "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-250",
    orange: "bg-orange-200 border-orange-400 text-orange-900 hover:bg-orange-250",
    teal: "bg-teal-200 border-teal-400 text-teal-900 hover:bg-teal-250",
    pink: "bg-pink-200 border-pink-400 text-pink-900 hover:bg-pink-250",
    indigo: "bg-indigo-200 border-indigo-400 text-indigo-900 hover:bg-indigo-250",
    gray: "bg-gray-200 border-gray-400 text-gray-900 hover:bg-gray-250",
  };
  return colorMap[color || ""] || "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250";
};

// Component to render a single event
const EventCard = ({
  event,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  refetch,
  access,
  style,
  compact = false,
  hideEventActions
}: {
  event: CalendarEvent;
  onViewEvent?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  refetch?: () => void;
  access?: string;
  style?: React.CSSProperties;
  compact?: boolean;
  hideEventActions?: boolean;
}) => (
  <div
    className={cn(
      "rounded-md border overflow-hidden shadow-sm transition-colors",
      compact ? "p-0.5 sm:p-1" : "p-1 sm:p-2",
      getColorClasses(event.color),
      onViewEvent && "cursor-pointer"
    )}
    style={style}
    onClick={(e) => {
      e.stopPropagation();
      onViewEvent?.(event);
    }}
  >
    <div className="flex items-start justify-between gap-1">
      <div
        className={cn(
          "font-medium truncate flex-1 cursor-pointer",
          compact ? "text-[10px]" : "text-xs"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onViewEvent?.(event);
        }}
      >
        {event.title}
      </div>
      {!hideEventActions && (
        <CalendarEventActions
          event={event}
          onEdit={onEditEvent}
          onView={onViewEvent}
          onDelete={onDeleteEvent}
          refetch={refetch}
          access={access}
          size="sm"
          variant="dropdown"
        />
      )}
    </div>
    {event.tags && !compact && (
      <div className="flex flex-wrap gap-1 mt-1">
        {event.tags.slice(0, 2).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1.5 py-0.5 rounded-sm text-white text-[10px]",
              `bg-${tag.color}-500`
            )}
          >
            {tag.label}
          </span>
        ))}
        {event.tags.length > 2 && (
          <span className="text-[10px] text-gray-500">+{event.tags.length - 2}</span>
        )}
      </div>
    )}
    {event.tags && compact && (
      <div className="flex gap-1 mt-0.5">
        {event.tags.slice(0, 1).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1 py-0.5 rounded-sm text-white text-[8px]",
              `bg-${tag.color}-500`
            )}
          >
            {tag.label}
          </span>
        ))}
      </div>
    )}
  </div>
);

export function WeekView({ currentDate, events, dateRange, onViewEvent, onEditEvent, onDeleteEvent, refetch, access, hideEventActions, onDateClick }: WeekViewProps) {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ day: Date; hour: number; events: CalendarEvent[] } | null>(null);
  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start from Monday to match slot generation
  const endDate = endOfWeek(startDate, { weekStartsOn: 1 });

  const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 to 23:00
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));


  // Get events for a specific day and hour
  const getEventsForTimeSlot = (day: Date, hour: number) => {
    const hourStart = addHours(startOfDay(day), hour);
    const hourEnd = addHours(startOfDay(day), hour + 1);

    return events.filter((event) => {
      const eventStart = new Date(event.start);

      // Only show the event in the hour slot where it starts
      // This prevents duplicates across multiple hour slots
      return isSameDay(eventStart, day) &&
        eventStart >= hourStart &&
        eventStart < hourEnd;
    });
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      <div className="sticky top-0 z-10 grid grid-cols-8 border-b bg-white shrink-0">
        <div className="border-r py-2 text-center text-xs"></div>
        {days.map((day, index) => (
          <div
            key={day.toString()}
            className={cn(
              "py-2 text-center text-sm border-r",
              // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
            )}
          >
            <p className="text-[10px] sm:text-xs"><span className="sm:hidden">{format(day, "EEE")}</span><span className="hidden sm:inline">{format(day, "EEEE")}</span></p>
            <p className="text-[10px] sm:text-[11px]">{format(day, "MMM d")}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-8">
          <div className="sticky left-0 z-10 border-r bg-white">
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-16 sm:h-24 border-b px-1 sm:px-2 text-xs text-gray-500 relative"
              >
                <div className="absolute -top-2.5">{hour}:00</div>
              </div>
            ))}
          </div>
          {days.map((day, index) => (
            <div
              key={day.toString()}
              className={cn(
                "border-r",
                // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
              )}
            >
              {hours.map((hour) => {
                const eventsForSlot = getEventsForTimeSlot(day, hour);
                const maxVisibleEvents = 2; // Show max 2 events directly in the slot

                return (
                  <div
                    key={hour}
                    className="h-16 sm:h-24 border-b p-0.5 sm:p-1 relative hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => {
                      const clickedDate = addHours(startOfDay(day), hour);
                      onDateClick?.(clickedDate);
                    }}
                  >
                    {eventsForSlot.length === 0 ? null : eventsForSlot.length === 1 ? (
                      // Single event - show full event
                      (() => {
                        const event = eventsForSlot[0];
                        const eventStart = new Date(event.start);
                        const eventEnd = new Date(event.end);
                        const startMinutes = eventStart.getMinutes();
                        const top = (startMinutes / 60) * 100;
                        const height = Math.min(
                          ((eventEnd.getTime() - eventStart.getTime()) / (1000 * 60 * 60)) * 100,
                          100 - top
                        );

                        return (
                          <div
                            key={event.id}
                            className="absolute left-1 right-1"
                            style={{
                              top: `${top}%`,
                              height: `${height}%`,
                              zIndex: 10
                            }}
                          >
                            <EventCard
                              event={event}
                              onViewEvent={onViewEvent}
                              onEditEvent={onEditEvent}
                              onDeleteEvent={onDeleteEvent}
                              refetch={refetch}
                              access={access}
                              hideEventActions={hideEventActions}
                            />
                          </div>
                        );
                      })()
                    ) : (
                      // Multiple events - show only "View All Events" button for cleaner UI
                      <div className="absolute inset-1 flex flex-col justify-center">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full h-full min-h-[40px] sm:min-h-[60px] bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200 hover:border-blue-300 text-blue-800 font-medium shadow-sm hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTimeSlot({ day, hour, events: eventsForSlot });
                              }}
                            >
                              <div className="text-sm sm:text-lg font-bold">{eventsForSlot.length}</div>
                              <div className="hidden sm:block text-xs">
                                {eventsForSlot.length === 1 ? 'Slot' : 'Slots'}
                              </div>
                              <div className="hidden sm:block text-[10px] opacity-70 text-nowrap">View more</div>
                            </Button>
                          </SheetTrigger>
                          <SheetContent fixCloseButton reverseCloseButton className="w-[400px] sm:w-[540px] bg-formbg p-0 custom-scrollbar h-full">
                            <SheetHeader className="p-4 sticky top-0 z-40 py-2  shadow-sm bg-white">
                              <div className="flex items-center justify-between gap-2 w-full">

                                <SheetTitle className="pl-6">
                                  Slots for {format(day, "EEEE, MMM d yyyy")}
                                </SheetTitle>
                              </div>

                            </SheetHeader>
                            <div className="h-full p-4">
                              <div className=" p-4 bg-white rounded-lg space-y-4  ">
                                <>
                                  <div className="text-sm font-medium text-gray-700">
                                    {eventsForSlot.length} Slots scheduled
                                  </div>
                                </>
                                {eventsForSlot.map((event, index) => (
                                  <EventCard
                                    event={event}
                                    onViewEvent={onViewEvent}
                                    onEditEvent={onEditEvent}
                                    onDeleteEvent={onDeleteEvent}
                                    refetch={refetch}
                                    access={access}
                                    hideEventActions={hideEventActions}
                                  />
                                ))}
                              </div>
                            </div>
                          </SheetContent>
                        </Sheet>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

